import { z } from "zod";
import { responseSchema } from "../http.js";
import { currencySchema, volumePointsSchema } from "./product.js";
import { PLAN_MAX_LEVELS, customerTypeSchema } from "./network.js";
import { DEFAULT_MEMBERSHIP_CLOCK_CONFIG, membershipClockConfigSchema, partnerActivationSchema, } from "./membership.js";
/**
 * ═══════════════════════════════════════════════════════════
 * El plan de compensación
 *
 * ── Esto es un PORTE, no un diseño nuevo ───────────────────
 * El plan es el del sistema actual: "Modelo Sostenible v6.2", tal como vive
 * en `commission_params/default` de producción. Los gradientes, los topes,
 * los rangos y la regla 40/60 están copiados de ahí. Lo que cambia es CÓMO
 * se guardan los números, no cuáles son.
 *
 * ── Ocho bonos, uno pagando ────────────────────────────────
 * La auditoría del 25-ago-2026 midió lo siguiente: de los ocho bonos del
 * plan, producción solo paga UNO. Las 169 comisiones que existen son todas
 * de inicio rápido. El bono de red de 8 niveles —el corazón del plan— nunca
 * ha pagado un peso, porque exige la regla 40/60 y desde mayo no la pasa
 * nadie: cero de 51, cero de 53, cero de 21.
 *
 * Ese dato manda sobre este archivo. Cada bono lleva escrito qué le hace
 * falta para poder calcularse de verdad, para que nadie vuelva a dar por
 * pagado un bono que en la práctica da cero.
 * ═══════════════════════════════════════════════════════════
 */
// ═══════════════════════════════════════════════════════════
// Dinero y porcentajes — enteros, siempre
// ═══════════════════════════════════════════════════════════
/**
 * Centavos enteros, igual que en productos y paquetes. Aquí importa aún
 * más: una comisión es un porcentaje de una suma que después se reparte
 * entre ocho niveles y se acumula durante meses. Un flotante se desvía y el
 * error solo aparece cuando el saldo de alguien no cuadra.
 */
export const amountCentsSchema = z
    .number()
    .int("Los importes se guardan en centavos enteros")
    .min(0, "Un importe no puede ser negativo")
    .max(1_000_000_000, "Ese importe es demasiado alto");
/**
 * PUNTOS BÁSICOS, no porcentajes. 10% = 1000 bps.
 *
 * Por qué no `percent: number`: el plan tiene topes como el 33.33% de los
 * fondos, y "33.33" en punto flotante multiplicado por un importe da
 * números que no cierran. En puntos básicos —3333— toda la aritmética del
 * plan es entera de principio a fin:
 *
 *     centavos = Math.floor(base * bps / 10_000)
 *
 * y no hay un solo flotante en el camino del dinero. Para enseñárselo a una
 * persona está `bpsToPercent`.
 */
export const bpsSchema = z
    .number()
    .int("Los porcentajes se guardan en puntos básicos enteros (10% = 1000)")
    .min(0)
    .max(10_000, "Un porcentaje no puede pasar del 100%");
export const BPS_DENOMINATOR = 10_000;
export function percentToBps(percent) {
    return Math.round(percent * 100);
}
export function bpsToPercent(bps) {
    return bps / 100;
}
export function formatBps(bps) {
    const percent = bpsToPercent(bps);
    return Number.isInteger(percent) ? `${percent}%` : `${percent.toFixed(2)}%`;
}
/**
 * El importe de una comisión. LA función: nadie más multiplica por un
 * porcentaje en todo el sistema.
 *
 * ── El redondeo, decidido una vez ──────────────────────────
 * Se TRUNCA hacia abajo, igual que `applyDiscount` en `package.ts`. Pero el
 * motivo aquí es el contrario y conviene decirlo, porque parecen la misma
 * regla y no lo son:
 *
 *   - En un precio, truncar deja el centavo suelto a favor de QUIEN COMPRA.
 *   - En una comisión, truncar deja el centavo suelto a favor de LA EMPRESA.
 *
 * Se trunca igual, y a propósito. Un plan de compensación reparte un
 * presupuesto acotado: el modelo v6.2 se sostiene sobre un techo de payout
 * del 25%. Si cada nivel redondeara hacia arriba, la suma de ocho niveles
 * podría pasarse del techo que la empresa puede pagar — se estaría creando
 * dinero que no existe, un centavo por nivel, en cada compra, todos los
 * meses. Truncar garantiza lo único que no se puede negociar:
 *
 *     suma de las comisiones ≤ base × porcentaje del plan
 *
 * El resto sub-centavo se queda donde estaba y no se inventa en ningún
 * lado. Es la misma disciplina de `package.ts` —que el redondeo nunca
 * fabrique valor— aplicada al lado del dinero que sale.
 */
export function commissionAmountCents(baseCents, bps) {
    if (baseCents <= 0 || bps <= 0)
        return 0;
    return Math.floor((baseCents * bps) / BPS_DENOMINATOR);
}
// ═══════════════════════════════════════════════════════════
// Rangos
// ═══════════════════════════════════════════════════════════
/**
 * De `I` (recién entrado) a `I16`. Es la escalera del plan v6.2.
 *
 * El sistema actual arrastra además los mismos rangos con prefijo `A` y una
 * función `normalizeRankId` para leer datos viejos. Aquí no: la base nace
 * vacía, así que solo existe la nomenclatura buena.
 */
export const RANK_IDS = [
    "I",
    "I1", "I2", "I3", "I4", "I5", "I6", "I7", "I8",
    "I9", "I10", "I11", "I12", "I13", "I14", "I15", "I16",
];
export const rankIdSchema = z.enum(RANK_IDS);
// ═══════════════════════════════════════════════════════════
// Los bonos
// ═══════════════════════════════════════════════════════════
/**
 * Los ocho bonos del plan, más el ajuste manual.
 *
 * Los ocho primeros son los de `BonusType` en producción. El ajuste manual
 * lo pide `docs/00_ARQUITECTURA.md` §4.4, que enumera los tipos del libro.
 *
 * El bono directo de franquicia (`BDF`, 30·10·9·5·2 por vender una
 * franquicia) se QUITÓ del plan el 14-sep-2026 por decisión de Anahí:
 * recomendar una franquicia ya no paga comisión. En su lugar, quien la
 * compra paga sus productos con descuento (`FRANCHISEE_DISCOUNT_PERCENT`
 * en `contracts/pricing.ts`).
 */
export const BONUS_TYPES = [
    "BIR",
    "BUR",
    "BAR",
    "FUND_GLOBAL",
    "FUND_LEGACY",
    "FUND_LEGEND",
    "LOYALTY",
    "MILESTONE_1M_USD",
    "MANUAL_ADJUSTMENT",
];
export const bonusTypeSchema = z.enum(BONUS_TYPES);
export const BONUS_TYPE_LABELS = {
    BIR: "Bono de inicio rápido",
    BUR: "Bono uninivel de regalías",
    BAR: "Bono de avance de rango",
    FUND_GLOBAL: "Fondo global",
    FUND_LEGACY: "Fondo legado",
    FUND_LEGEND: "Fondo leyenda",
    LOYALTY: "Lealtad",
    MILESTONE_1M_USD: "Hito del millón de dólares",
    MANUAL_ADJUSTMENT: "Ajuste manual",
};
/**
 * Cuándo nace cada bono. Determina quién lo calcula y por eso decide qué se
 * puede construir hoy y qué espera al motor de pagos.
 *
 * - `purchase`: nace al pagarse una compra. Se calcula ya.
 * - `monthly_cut`: nace en el corte del mes. Necesita el corte, que es fase
 *   siguiente.
 * - `achievement`: nace al cruzar un umbral (rango, volumen acumulado).
 * - `manual`: lo escribe una persona.
 */
export const BONUS_TRIGGERS = ["purchase", "monthly_cut", "achievement", "manual"];
export const bonusTriggerSchema = z.enum(BONUS_TRIGGERS);
export const BONUS_TRIGGERS_BY_TYPE = {
    BIR: "purchase",
    BUR: "monthly_cut",
    BAR: "monthly_cut",
    FUND_GLOBAL: "monthly_cut",
    FUND_LEGACY: "monthly_cut",
    FUND_LEGEND: "monthly_cut",
    LOYALTY: "monthly_cut",
    MILESTONE_1M_USD: "achievement",
    MANUAL_ADJUSTMENT: "manual",
};
/**
 * Qué le falta a cada bono para pagar de verdad. Está en el contrato —y no
 * en un documento suelto— porque es lo que el panel tiene que poder
 * enseñar: un bono "habilitado" que no puede calcularse es exactamente cómo
 * producción llegó a tener siete bonos apagados sin que nadie lo notara.
 *
 * `null` = calculable hoy con lo que hay construido.
 */
export const BONUS_BLOCKERS = {
    BIR: null,
    MANUAL_ADJUSTMENT: null,
    BUR: "El reparto por niveles ya está escrito y probado, pero solo corre en el " +
        "corte mensual: necesita el acumulado del periodo (volumen personal, " +
        "clientes externos, volumen de clientes) y el rango vigente de cada quien.",
    BAR: "Necesita el corte mensual, el historial de rangos y el fondo del 3% del " +
        "volumen comisionable del mes.",
    FUND_GLOBAL: "Necesita el volumen comisionable total del mes y el censo de quiénes " +
        "califican, para repartir un fondo entre ellos con su tope por persona.",
    FUND_LEGACY: "Igual que el fondo global, más dos meses consecutivos calificados.",
    FUND_LEGEND: "Igual que el fondo global, exclusivo del rango I16.",
    LOYALTY: "Necesita la suscripción de recompra: el porcentaje depende de cuántos " +
        "meses seguidos lleva pagando la persona.",
    MILESTONE_1M_USD: "Necesita el volumen de red acumulado de por vida. Además, hoy su importe " +
        "está configurado en cero: aunque se calcule, no paga nada.",
};
// ═══════════════════════════════════════════════════════════
// Los parámetros del plan — lo que edita el super-admin
// ═══════════════════════════════════════════════════════════
/**
 * Equivalente de `commission_params/default`. Un solo documento, editable
 * desde el panel, del que cuelga TODO el dinero del plan.
 */
// ── Bono de inicio rápido ────────────────────────────────
export const birLevelSchema = z.object({
    level: z.number().int().min(1).max(PLAN_MAX_LEVELS),
    bps: bpsSchema,
});
/**
 * BIR — se paga sobre la primera compra de un socio nuevo, dentro de su
 * primer mes. Gradiente oficial v6.2: 20 · 4 · 6 · 4 · 2 = 36%.
 *
 * Es el ÚNICO bono que produccción está pagando: las 169 comisiones que
 * existen son todas de este tipo.
 *
 * `requiresRule4060` va en `false` por la decisión de negocio de junio de
 * 2026: el inicio rápido se paga al patrocinador ACTIVO sin exigirle
 * clientes externos. La regla 40/60 quedó SOLO para el bono de red.
 */
export const birConfigSchema = z.object({
    enabled: z.boolean(),
    eligibilityDays: z.number().int().min(1).max(365),
    onlyDistributorsGenerate: z.boolean(),
    requiresRule4060: z.boolean(),
    levels: z.array(birLevelSchema).min(1).max(PLAN_MAX_LEVELS),
});
// ── Bono uninivel de regalías ────────────────────────────
export const burLevelSchema = z.object({
    level: z.number().int().min(1).max(PLAN_MAX_LEVELS),
    bps: bpsSchema,
});
/**
 * BUR — el bono de red, 8 niveles, gradiente 10 · 5 · 4 · 4 · 3 · 3 · 2 · 1
 * = 32% para quien está en I11 o más arriba.
 *
 * Es el corazón del plan y el que NUNCA ha pagado. Dos compuertas lo
 * bloquean a la vez:
 *
 *   1. `rankLevelUnlocks`: tu rango dice hasta qué nivel cobras.
 *   2. La regla 40/60, que hoy no pasa nadie.
 *
 * `maxCompression`: si un patrocinador no califica, su parte NO se pierde —
 * sube al siguiente que sí califique, hasta dos saltos. Es lo que evita que
 * un socio inactivo en medio de la línea deje sin cobrar a todos los de
 * arriba.
 */
export const burConfigSchema = z.object({
    enabled: z.boolean(),
    maxLevels: z.number().int().min(1).max(PLAN_MAX_LEVELS),
    maxCompression: z.number().int().min(0).max(PLAN_MAX_LEVELS),
    levels: z.array(burLevelSchema).min(1).max(PLAN_MAX_LEVELS),
    /** Cuántos niveles desbloquea cada rango. */
    rankLevelUnlocks: z.record(rankIdSchema, z.number().int().min(0).max(PLAN_MAX_LEVELS)),
});
// ── Bono de avance de rango ──────────────────────────────
export const barMilestoneSchema = z.object({
    rankId: rankIdSchema,
    amountCents: amountCentsSchema,
    /** `product_credits` no es dinero: son créditos para canjear por producto. */
    payoutKind: z.enum(["cash", "product_credits"]),
});
export const barConfigSchema = z.object({
    enabled: z.boolean(),
    /** Qué parte del volumen comisionable del mes se reserva para pagarlo. */
    fundBpsOfCommissionableVolume: bpsSchema,
    requiredQualifiedPriorMonths: z.number().int().min(0).max(12),
    milestones: z.array(barMilestoneSchema),
});
// ── Los tres fondos ──────────────────────────────────────
export const fundConfigSchema = z.object({
    enabled: z.boolean(),
    bpsOfCommissionableVolume: bpsSchema,
    minRank: rankIdSchema,
    maxRank: rankIdSchema.nullable(),
    /** Nadie se lleva más de esta parte del fondo, por alto que sea su rango. */
    capPerUserBps: bpsSchema,
    minConsecutiveQualifiedMonths: z.number().int().min(0).max(24),
});
export const globalFundsConfigSchema = z.object({
    enabled: z.boolean(),
    fundGlobal: fundConfigSchema,
    fundLegacy: fundConfigSchema,
    fundLegend: fundConfigSchema,
});
// ── Lealtad ──────────────────────────────────────────────
export const loyaltyTierSchema = z.object({
    minMonth: z.number().int().min(1),
    /** `null` = de ahí en adelante. */
    maxMonth: z.number().int().min(1).nullable(),
    creditBps: bpsSchema,
});
export const loyaltyConfigSchema = z.object({
    enabled: z.boolean(),
    bpsOfPrice: bpsSchema,
    tiers: z.array(loyaltyTierSchema),
    fastTrackVolumePoints: volumePointsSchema,
    fastTrackStartMonth: z.number().int().min(1),
    /** Hoy solo canjeables por producto; no salen como dinero. */
    creditKind: z.enum(["product_only", "cash_convertible"]),
});
// ── El hito del millón ───────────────────────────────────
export const millionMilestoneConfigSchema = z.object({
    enabled: z.boolean(),
    /** Volumen de red acumulado, en centavos de dólar. */
    thresholdCentsUSD: amountCentsSchema,
    /**
     * En producción esto vale CERO. Es decir: el hito se marca y no paga
     * nada. Se conserva tal cual —el plan lo contempla y el whitepaper lo
     * documenta— pero el panel debe enseñar que está en cero.
     */
    bonusAmountCents: amountCentsSchema,
});
// ── La regla 40/60 ───────────────────────────────────────
/**
 * La compuerta que hoy no pasa nadie. Exige TRES cosas a la vez:
 *
 *   1. Volumen personal ≥ 50 puntos      → lo cumplen 3 de 53 (6%)
 *   2. Al menos 3 clientes externos      → lo cumplen 0 de 53 (0%)
 *   3. ≥30% del volumen viene de clientes → lo cumplen 0 de 53 (0%)
 *
 * Los ceros de las dos últimas no son casualidad: en producción NINGUNO de
 * los 69 usuarios tiene un solo cliente externo, porque el registro
 * convierte en socio a todo el que entra por invitación.
 *
 * `network.ts` corrige la causa —el tipo de cuenta ya no se deduce del
 * enlace—, pero los números de la regla en sí son una decisión de negocio
 * que sigue ABIERTA. No se tocan aquí.
 */
export const rule4060ConfigSchema = z.object({
    enabled: z.boolean(),
    minPersonalVolumePoints: volumePointsSchema,
    minExternalClients: z.number().int().min(0).max(100),
    clientVolumeBps: z.object({
        year1: bpsSchema,
        year2Plus: bpsSchema,
    }),
    currentYear: z.number().int().min(1),
    /** Qué bonos bloquea si no se pasa. */
    blocks: z.array(bonusTypeSchema),
});
// ── Inactividad y pagos ──────────────────────────────────
export const inactivityConfigSchema = z.object({
    enabled: z.boolean(),
    monthsWithoutActivity: z.number().int().min(1).max(36),
});
export const payoutConfigSchema = z.object({
    minPayoutCents: amountCentsSchema,
    currency: currencySchema,
    maxRequestsPerDay: z.number().int().min(1).max(20),
    requiresKycApproved: z.boolean(),
});
/**
 * El corsé económico del modelo v6.2. No calcula nada por sí solo: es lo
 * que permite avisar en el panel cuando alguien sube un gradiente por
 * encima de lo que el negocio puede pagar.
 */
export const economicConstraintsSchema = z.object({
    minGrossMarginBps: bpsSchema,
    maxTotalPayoutBps: bpsSchema,
    opexBps: bpsSchema,
    loyaltyReserveBps: bpsSchema,
});
/**
 * Sobre qué se aplica el porcentaje del bono.
 *
 * - `price`: sobre lo que la persona pagó, en centavos. Es el de producción.
 * - `volume_points`: sobre los puntos, convertidos a dinero.
 *
 * Que sea configurable no es un capricho: cambia el importe de TODAS las
 * comisiones y por eso vive en los parámetros, versionado, y no en una
 * constante escondida en el servicio.
 */
export const COMMISSION_BASES = ["price", "volume_points"];
export const commissionBaseSchema = z.enum(COMMISSION_BASES);
export const commissionParamsSchema = z.object({
    id: z.literal("default"),
    modelName: z.string(),
    currency: currencySchema,
    commissionBase: commissionBaseSchema,
    /** Cuántos centavos vale un punto de volumen, si la base son puntos. */
    centsPerVolumePoint: amountCentsSchema,
    bir: birConfigSchema,
    bur: burConfigSchema,
    bar: barConfigSchema,
    globalFunds: globalFundsConfigSchema,
    loyalty: loyaltyConfigSchema,
    millionMilestone: millionMilestoneConfigSchema,
    rule4060: rule4060ConfigSchema,
    inactivity: inactivityConfigSchema,
    payout: payoutConfigSchema,
    /**
     * El reloj de recompra. Con valor por omisión a propósito: el plan ya
     * guardado en las bases de desarrollo no lo trae, y un plan sin reloj
     * no es un plan inválido — es uno con el ciclo de fábrica (30 días).
     */
    membership: membershipClockConfigSchema.default(DEFAULT_MEMBERSHIP_CLOCK_CONFIG),
    economicConstraints: economicConstraintsSchema,
    updatedAt: z.iso.datetime(),
    updatedByUserId: z.string().nullable(),
});
/** El PATCH del panel: todo opcional, nada con valor por omisión. */
export const commissionParamsUpdateSchema = commissionParamsSchema
    .omit({ id: true, updatedAt: true, updatedByUserId: true })
    .partial();
/**
 * El plan v6.2 tal como está en producción, ya en centavos y puntos
 * básicos. Es la semilla de `commission_params/default` y el valor contra
 * el que se comparan los cambios del panel.
 */
export const DEFAULT_COMMISSION_PARAMS = {
    id: "default",
    modelName: "Modelo Sostenible v6.2",
    currency: "MXN",
    commissionBase: "price",
    centsPerVolumePoint: 100,
    bir: {
        enabled: true,
        eligibilityDays: 30,
        onlyDistributorsGenerate: true,
        // Decisión de negocio de junio de 2026: el inicio rápido quedó exento.
        requiresRule4060: false,
        levels: [
            { level: 1, bps: 2000 },
            { level: 2, bps: 400 },
            { level: 3, bps: 600 },
            { level: 4, bps: 400 },
            { level: 5, bps: 200 },
        ],
    },
    bur: {
        enabled: true,
        maxLevels: 8,
        maxCompression: 2,
        levels: [
            { level: 1, bps: 1000 },
            { level: 2, bps: 500 },
            { level: 3, bps: 400 },
            { level: 4, bps: 400 },
            { level: 5, bps: 300 },
            { level: 6, bps: 300 },
            { level: 7, bps: 200 },
            { level: 8, bps: 100 },
        ],
        rankLevelUnlocks: {
            I: 1, I1: 1, I2: 2, I3: 3, I4: 3, I5: 4, I6: 4, I7: 5,
            I8: 5, I9: 6, I10: 7, I11: 8, I12: 8, I13: 8, I14: 8, I15: 8, I16: 8,
        },
    },
    bar: {
        enabled: true,
        fundBpsOfCommissionableVolume: 300,
        requiredQualifiedPriorMonths: 2,
        milestones: [
            { rankId: "I2", amountCents: 10_000, payoutKind: "product_credits" },
            { rankId: "I4", amountCents: 525_000, payoutKind: "cash" },
            { rankId: "I6", amountCents: 1_050_000, payoutKind: "cash" },
            { rankId: "I8", amountCents: 2_100_000, payoutKind: "cash" },
            { rankId: "I10", amountCents: 5_250_000, payoutKind: "cash" },
        ],
    },
    globalFunds: {
        enabled: true,
        fundGlobal: {
            enabled: true,
            bpsOfCommissionableVolume: 700,
            minRank: "I6",
            maxRank: null,
            capPerUserBps: 2500,
            minConsecutiveQualifiedMonths: 0,
        },
        fundLegacy: {
            enabled: true,
            bpsOfCommissionableVolume: 100,
            minRank: "I11",
            maxRank: "I15",
            capPerUserBps: 3333,
            minConsecutiveQualifiedMonths: 2,
        },
        fundLegend: {
            enabled: true,
            bpsOfCommissionableVolume: 100,
            minRank: "I16",
            maxRank: "I16",
            capPerUserBps: 3333,
            minConsecutiveQualifiedMonths: 0,
        },
    },
    loyalty: {
        enabled: true,
        bpsOfPrice: 500,
        tiers: [
            { minMonth: 1, maxMonth: 3, creditBps: 2000 },
            { minMonth: 4, maxMonth: 9, creditBps: 2500 },
            { minMonth: 10, maxMonth: null, creditBps: 3000 },
        ],
        fastTrackVolumePoints: 475,
        fastTrackStartMonth: 4,
        creditKind: "product_only",
    },
    millionMilestone: {
        enabled: true,
        thresholdCentsUSD: 100_000_000,
        // Cero en producción: se marca el hito, no se paga nada.
        bonusAmountCents: 0,
    },
    rule4060: {
        enabled: true,
        minPersonalVolumePoints: 50,
        minExternalClients: 3,
        clientVolumeBps: { year1: 3000, year2Plus: 4000 },
        currentYear: 1,
        blocks: ["BUR"],
    },
    inactivity: { enabled: true, monthsWithoutActivity: 6 },
    payout: {
        minPayoutCents: 20_000,
        currency: "MXN",
        maxRequestsPerDay: 3,
        requiresKycApproved: true,
    },
    membership: DEFAULT_MEMBERSHIP_CLOCK_CONFIG,
    economicConstraints: {
        minGrossMarginBps: 5300,
        maxTotalPayoutBps: 2500,
        opexBps: 2000,
        loyaltyReserveBps: 500,
    },
};
// ═══════════════════════════════════════════════════════════
// La regla 40/60, evaluada
// ═══════════════════════════════════════════════════════════
export const periodStatsSchema = z.object({
    personalVolumePoints: volumePointsSchema,
    /** Cuántas personas DISTINTAS que no son socios le compraron este mes. */
    externalClients: z.number().int().min(0),
    /** Cuántos de esos puntos vienen de clientes externos. */
    clientVolumePoints: volumePointsSchema,
});
export const rule4060ResultSchema = z.object({
    passed: z.boolean(),
    volumeMet: z.boolean(),
    clientsMet: z.boolean(),
    clientShareMet: z.boolean(),
    requiredClientShareBps: bpsSchema,
    actualClientShareBps: z.number().int().min(0),
    /** Qué faltó, en palabras. Se guarda: sin esto no se puede explicar un cero. */
    reasons: z.array(z.string()),
});
/**
 * Evalúa la regla. Función PURA: sin base de datos, sin reloj, sin sesión.
 *
 * Vive en el contrato compartido para que el panel del socio pueda
 * enseñarle "te faltan 2 clientes" con exactamente la misma aritmética con
 * la que el servidor decide si cobra. Dos implementaciones de esta regla
 * —una en cada lado— es como se acaba enseñando "calificas" a alguien que
 * no cobra.
 */
export function evaluateRule4060(stats, config) {
    if (!config.enabled) {
        return {
            passed: true,
            volumeMet: true,
            clientsMet: true,
            clientShareMet: true,
            requiredClientShareBps: 0,
            actualClientShareBps: BPS_DENOMINATOR,
            reasons: [],
        };
    }
    const required = config.currentYear >= 2 ? config.clientVolumeBps.year2Plus : config.clientVolumeBps.year1;
    const volumeMet = stats.personalVolumePoints >= config.minPersonalVolumePoints;
    const clientsMet = stats.externalClients >= config.minExternalClients;
    // Sin volumen personal la proporción no está definida. Se toma como cero
    // —no como "cumple"— porque el bono se paga sobre volumen: sin volumen no
    // hay nada que repartir, y un 0/0 tratado como 100% haría calificar a
    // quien no vendió nada.
    const actual = stats.personalVolumePoints > 0
        ? Math.floor((stats.clientVolumePoints * BPS_DENOMINATOR) / stats.personalVolumePoints)
        : 0;
    const clientShareMet = actual >= required;
    const reasons = [];
    if (!volumeMet) {
        reasons.push(`Volumen personal ${stats.personalVolumePoints} de ${config.minPersonalVolumePoints} puntos`);
    }
    if (!clientsMet) {
        reasons.push(`${stats.externalClients} clientes externos de ${config.minExternalClients}`);
    }
    if (!clientShareMet) {
        reasons.push(`${formatBps(actual)} del volumen viene de clientes; hace falta ${formatBps(required)}`);
    }
    return {
        passed: volumeMet && clientsMet && clientShareMet,
        volumeMet,
        clientsMet,
        clientShareMet,
        requiredClientShareBps: required,
        actualClientShareBps: actual,
        reasons,
    };
}
// ═══════════════════════════════════════════════════════════
// La comisión devengada — el asiento del libro
// ═══════════════════════════════════════════════════════════
/**
 * `pending` → `released` → `paid`, y desde cualquiera → `rejected`.
 *
 * Nada se paga solo. Una comisión nace `pending` y alguien tiene que
 * liberarla; el dinero solo sale en `paid`. Es el mismo ciclo del sistema
 * actual, y se conserva a propósito: es la única razón por la que los
 * $779.80 del patrocinador fantasma se quedaron atrapados en vez de salir
 * de la cuenta bancaria.
 */
/*
 * `forfeited` (Anahí, 22-oct-2026): «solo si hace su recompra mensual puede
 * cobrar comisiones; si no la hace, pierde dichas comisiones». Es la
 * pérdida POR REGLA cuando vence el reloj de recompra, distinta de la
 * anulación a mano (`rejected`).
 */
export const COMMISSION_STATUSES = ["pending", "released", "paid", "rejected", "forfeited"];
export const commissionStatusSchema = z.enum(COMMISSION_STATUSES);
export const COMMISSION_STATUS_LABELS = {
    pending: "Por liberar",
    released: "Liberada",
    paid: "Pagada",
    rejected: "Anulada",
    forfeited: "Perdida (sin recompra)",
};
/**
 * Una comisión devengada.
 *
 * Es un asiento de un libro de SOLO AGREGADO (`docs/00_ARQUITECTURA.md`
 * §4.4): se escribe una vez y el saldo de cada socio es una proyección
 * derivada de estos asientos, nunca la fuente de verdad. Así cualquier
 * discrepancia se reconstruye desde el origen.
 *
 * Por eso lleva tanto contexto congelado —el rango de entonces, el
 * porcentaje aplicado, el nivel— aunque se pudiera recalcular: el día que
 * alguien pregunte "¿por qué cobré esto?", la respuesta tiene que estar en
 * el asiento y no depender de que los parámetros no hayan cambiado desde
 * entonces.
 */
export const commissionSchema = z.object({
    id: z.string(),
    /** A quién se le debe. */
    userId: z.string(),
    bonusType: bonusTypeSchema,
    status: commissionStatusSchema,
    amountCents: amountCentsSchema,
    currency: currencySchema,
    /** Qué la disparó. `null` en los bonos que nacen del corte, no de una compra. */
    sourceOrderId: z.string().nullable(),
    /** Quién compró. `null` en los bonos que no vienen de una compra. */
    sourceUserId: z.string().nullable(),
    /** Nivel de separación entre quien cobra y quien compró. 1 = directo. */
    level: z.number().int().min(0).max(PLAN_MAX_LEVELS).nullable(),
    bpsApplied: bpsSchema,
    baseCents: amountCentsSchema,
    /** Cuántos niveles se saltaron por compresión antes de llegar a esta persona. */
    compressionUsed: z.number().int().min(0).default(0),
    /** El rango de quien cobra, congelado. */
    rankAtTime: rankIdSchema,
    /** `2026-08`. Agrupa por corte. */
    periodId: z.string().regex(/^\d{4}-\d{2}$/),
    createdAt: z.iso.datetime(),
    releasedAt: z.iso.datetime().nullable(),
    paidAt: z.iso.datetime().nullable(),
});
/** El periodo de una fecha: `2026-08`. */
export function periodIdOf(date) {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    return `${year}-${month}`;
}
// ═══════════════════════════════════════════════════════════
// Lo que NO se pagó, y por qué
// ═══════════════════════════════════════════════════════════
/**
 * Por qué alguien de la línea no cobró.
 *
 * No es un detalle de registro: es el dato que sostiene la pantalla de "lo
 * que has dejado de ganar" que pide
 * `docs/01_MODELO_FRANQUICIA_Y_ONBOARDING.md`, y es lo que le habría dicho
 * a alguien, hace meses, que el bono de red no estaba pagando. Un cero sin
 * motivo escrito es indistinguible de un cero por avería — que es
 * exactamente lo que pasó en producción durante cuatro meses.
 */
export const SKIP_REASONS = [
    "NO_SPONSOR",
    "LEVEL_NOT_IN_PLAN",
    "BONUS_DISABLED",
    "BUYER_NOT_DISTRIBUTOR",
    "OUTSIDE_FIRST_MONTH",
    "UPLINE_INACTIVE",
    "INACTIVE_PARTNER",
    "NO_MEMBERSHIP",
    "RULE_4060_FAILED",
    "RANK_LOCKS_LEVEL",
    "AMOUNT_ROUNDS_TO_ZERO",
    "MAX_COMPRESSION_REACHED",
];
export const skipReasonSchema = z.enum(SKIP_REASONS);
export const SKIP_REASON_LABELS = {
    NO_SPONSOR: "No hay nadie más arriba en la red",
    LEVEL_NOT_IN_PLAN: "Ese nivel está fuera de lo que paga el plan",
    BONUS_DISABLED: "El bono está apagado en la configuración",
    BUYER_NOT_DISTRIBUTOR: "Quien compró no es socio, y este bono solo lo generan los socios",
    OUTSIDE_FIRST_MONTH: "La compra cae fuera del primer mes de quien compró",
    UPLINE_INACTIVE: "La cuenta que cobraría no está activa",
    INACTIVE_PARTNER: "No hizo su recompra del mes (mínimo de puntos del plan): este mes es cliente, no socio",
    NO_MEMBERSHIP: "Sin membresía anual vigente: sin ella no se cobran comisiones",
    RULE_4060_FAILED: "No pasa la regla 40/60",
    RANK_LOCKS_LEVEL: "Su rango todavía no desbloquea ese nivel",
    AMOUNT_ROUNDS_TO_ZERO: "El importe no llega ni a un centavo",
    MAX_COMPRESSION_REACHED: "Se agotaron los saltos de compresión",
};
// ═══════════════════════════════════════════════════════════
// Lo que devuelve el cálculo
// ═══════════════════════════════════════════════════════════
/**
 * Una comisión ANTES de existir en la base. El cálculo es puro y no escribe
 * nada: devuelve intenciones, y otra capa —con su transacción y su llave de
 * idempotencia— decide si se guardan.
 */
export const commissionDraftSchema = commissionSchema.omit({
    id: true,
    status: true,
    createdAt: true,
    releasedAt: true,
    paidAt: true,
});
export const skippedBeneficiarySchema = z.object({
    userId: z.string(),
    bonusType: bonusTypeSchema,
    level: z.number().int().min(1).max(PLAN_MAX_LEVELS),
    reason: skipReasonSchema,
    /** Lo que habría cobrado. Es la cifra de "lo que has dejado de ganar". */
    forgoneCents: amountCentsSchema,
    detail: z.string().optional(),
});
export const commissionCalculationSchema = z.object({
    commissions: z.array(commissionDraftSchema),
    skipped: z.array(skippedBeneficiarySchema),
    totalCents: amountCentsSchema,
    baseCents: amountCentsSchema,
});
// ═══════════════════════════════════════════════════════════
// API
// ═══════════════════════════════════════════════════════════
export const commissionListQuerySchema = z.object({
    bonusType: bonusTypeSchema.optional(),
    status: commissionStatusSchema.optional(),
    /** En qué moneda nació la comisión (14-sep-2026: el libro se filtra por moneda). */
    currency: currencySchema.optional(),
    periodId: z
        .string()
        .regex(/^\d{4}-\d{2}$/, "El periodo se escribe como 2026-08")
        .optional(),
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(100).default(25),
});
export const commissionListSchema = z.object({
    items: z.array(commissionSchema),
    total: z.number().int().min(0),
    page: z.number().int().min(1),
    perPage: z.number().int().min(1),
    /** Suma por estado, en centavos. Es el "cuánto tengo" del panel. */
    totalsByStatus: z.record(commissionStatusSchema, amountCentsSchema),
    /**
     * `userId → nombre`, SOLO en el listado del panel: el admin ve el
     * libro de todos y un id crudo no le dice nada. En "mis comisiones"
     * no viaja — cada quien ya sabe quién es.
     */
    names: z.record(z.string(), z.string()).optional(),
});
/** Lo que un socio ve de sí mismo. Se DERIVA del libro; no se guarda. */
/**
 * El saldo de UNA moneda. Las comisiones nacen en la moneda de la compra
 * que las generó, y un socio con invitados en los dos países tiene dos
 * saldos que NO se suman: 123 dólares y 10,479 pesos no son "10,602" de
 * nada. (Bug cerrado el 14-sep-2026 — `docs/08`, prioridad 1.)
 */
export const currencyBalanceSchema = z.object({
    currency: currencySchema,
    pendingCents: amountCentsSchema,
    releasedCents: amountCentsSchema,
    paidCents: amountCentsSchema,
});
export const commissionSummarySchema = z.object({
    userId: z.string(),
    /**
     * La moneda PRINCIPAL del socio (la de su país) y sus cifras. Es lo que
     * las tarjetas pintan en grande; `balances` trae todas las monedas con
     * movimiento, para la línea de "y además … USD".
     */
    currency: currencySchema,
    pendingCents: amountCentsSchema,
    releasedCents: amountCentsSchema,
    paidCents: amountCentsSchema,
    /** Un saldo por moneda con movimiento; la principal siempre va, aunque sea en cero. */
    balances: z.array(currencyBalanceSchema).min(1),
    /** Lo que habría cobrado y no cobró. El argumento de venta de la franquicia. */
    forgoneCents: amountCentsSchema,
    customerType: customerTypeSchema,
    rankId: rankIdSchema,
    /** ¿Este mes es socio activo (cobra) o cliente (no cobra)? Ver `contracts/membership.ts`. */
    activation: partnerActivationSchema,
});
export const commissionParamsResponseSchema = responseSchema(z.object({ params: commissionParamsSchema }));
export const commissionListResponseSchema = responseSchema(commissionListSchema);
export const commissionSummaryResponseSchema = responseSchema(z.object({ summary: commissionSummarySchema }));
export const COMMISSION_ROUTES = {
    /** Las comisiones de quien pide. */
    mine: "/api/commissions/me",
    summary: "/api/commissions/me/summary",
    adminList: "/api/admin/commissions",
    /** El TOP de socios por comisiones generadas — el dashboard lo pinta. */
    adminTop: "/api/admin/commissions/top-earners",
    /** El libro agrupado por persona: una fila, un total. */
    adminByUser: "/api/admin/commissions/por-persona",
    adminParams: "/api/admin/commission-params",
    /** El corte mensual del bono de red (14-sep-2026): correrlo y ver las corridas. */
    adminMonthlyCut: "/api/admin/commissions/monthly-cut",
    adminMonthlyCuts: "/api/admin/commissions/monthly-cuts",
};
// ── El corte mensual ─────────────────────────────────────
export const monthlyCutRequestSchema = z.object({
    /** "2026-08". Ausente = el mes anterior al de hoy. */
    periodId: z.string().regex(/^\d{4}-\d{2}$/, "El periodo se escribe como 2026-08").optional(),
    /** Solo contar, sin escribir asientos. */
    dryRun: z.boolean().optional(),
});
export const monthlyCutResultSchema = z.object({
    periodId: z.string(),
    dryRun: z.boolean(),
    ordersInPeriod: z.number().int().min(0),
    repurchases: z.number().int().min(0),
    firstPurchasesSkipped: z.number().int().min(0),
    activePartners: z.number().int().min(0),
    commissionsInserted: z.number().int().min(0),
    commissionsAlreadyThere: z.number().int().min(0),
    totalCentsByCurrency: z.record(z.string(), z.number().int().min(0)),
    forgoneCentsByCurrency: z.record(z.string(), z.number().int().min(0)),
    skippedByReason: z.record(z.string(), z.number().int().min(0)),
});
export const monthlyCutResponseSchema = responseSchema(z.object({ result: monthlyCutResultSchema }));
export const monthlyCutsResponseSchema = responseSchema(z.object({
    runs: z.array(z.object({ id: z.string(), periodId: z.string(), ranAt: z.iso.datetime(), summary: monthlyCutResultSchema })),
}));
/**
 * Una fila del TOP de socios (Anahí, 27-ago: "el top de socios que más
 * han generado comisiones"): lo generado en total, lo ya cubierto y lo
 * que se le debe. Anuladas fuera — una comisión rechazada no se generó.
 */
export const topEarnerSchema = z.object({
    userId: z.string(),
    name: z.string(),
    /** En qué moneda está ESTA fila (14-sep-2026): una persona con pesos y dólares es dos filas. */
    currency: currencySchema,
    totalCents: z.number().int().min(0),
    paidCents: z.number().int().min(0),
    owedCents: z.number().int().min(0),
    count: z.number().int().min(0),
});
export const topEarnersResponseSchema = responseSchema(z.object({ items: z.array(topEarnerSchema) }));
/**
 * ═══════════════════════════════════════════════════════════════════
 * El libro AGRUPADO POR PERSONA (Anahí, 29-ago).
 *
 * "Se deben mostrar las comisiones por usuarios, o sea por correos, y
 * cuando ingreses al usuario ahí muestras su historia."
 *
 * La lista era un renglón por COMISIÓN, así que la misma persona
 * aparecía cinco veces con cinco montos y no había forma de leer cuánto
 * lleva ganado sin sumar a mano. Agrupada, cada fila es una persona y su
 * total; el desglose vive donde ya vivía —su expediente—, que es donde
 * tiene sentido leer bono por bono.
 *
 * Lleva el CORREO además del nombre: dos socias pueden llamarse igual y
 * el correo es lo que no se repite.
 * ═══════════════════════════════════════════════════════════════════
 */
export const commissionsByUserSchema = topEarnerSchema.extend({
    email: z.string(),
    /** Lo último que generó: para ordenar por actividad reciente. */
    lastAt: z.iso.datetime().nullable(),
});
export const commissionsByUserResponseSchema = responseSchema(z.object({
    items: z.array(commissionsByUserSchema),
    total: z.number().int().min(0),
    page: z.number().int().min(1),
    perPage: z.number().int().min(1),
}));
//# sourceMappingURL=commission.js.map